import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid, makeStyles } from "@material-ui/core";
import { useLoanStore } from '@roc/feature-loans';
import { ConfirmDialog, Paper, PaperBorderType, SelectField } from '@roc/ui';

const useStyles = makeStyles(() => ({
  gridContainer: {
    padding: '0 16px 16px 16px',
  },
  selectFieldContainer: {
    '& .MuiFormControl-root.MuiFormControl-fullWidth': {
      padding: '0 5px 0 10px',
      borderRadius: '5px',
      border: '1px solid #E4E4E7 !important',
      '& .MuiInput-underline': {
        '&:before': {
          transition: 'none !important',
          borderBottom: 'none !important',
        },
        '&:after': {
          transform: 'none !important',
          transition: 'none !important',
          borderBottom: 'none !important',
        },
      },
    },
  },
}));

interface SpecialistOption {
  label: string;
  value: string;
}

interface Loan {
  loanId: number;
  drawAdminSpecialistFullName?: string;
  drawReviewSpecialistFullName?: string;
}

interface SpecialistsDropdown {
  drawAdminSpecialists?: SpecialistOption[];
  drawReviewSpecialists?: SpecialistOption[];
}

interface LoanActorsProps {
  loan?: Loan;
  specialistsDropdown?: SpecialistsDropdown;
}

export default function LoanActors({ loan, specialistsDropdown }: LoanActorsProps) {
  const classes = useStyles();
  const { loanStore } = useLoanStore();

  const [selectedAdminSpecialist, setSelectedAdminSpecialist] = useState<SpecialistOption | undefined | null>();
  const [selectedReviewSpecialist, setSelectedReviewSpecialist] = useState<SpecialistOption | undefined | null>();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  const adminSpecialistOptions = useMemo(() => specialistsDropdown?.drawAdminSpecialists, [specialistsDropdown]);
  const reviewSpecialistOptions = useMemo(() => specialistsDropdown?.drawReviewSpecialists, [specialistsDropdown]);

  const handleConfirmRef = useRef<() => Promise<void>>();

  const updateSpecialists = useCallback((adminSpecialistOption?: SpecialistOption, reviewSpecialistOption?: SpecialistOption) => {
    return loanStore.updateSpecialists(loan?.loanId,
      {
        drawAdminSpecialistId: adminSpecialistOption?.value ? +adminSpecialistOption?.value : undefined,
        drawReviewSpecialistId: reviewSpecialistOption?.value ? +reviewSpecialistOption?.value : undefined,
      });
  }, [loanStore, loan]);

  const confirmUpdateSpecialists = useCallback(async (adminSpecialist?: SpecialistOption, reviewSpecialist?: SpecialistOption) => {
    updateSpecialists(adminSpecialist, reviewSpecialist);

    setSelectedAdminSpecialist((prevAdminSpecialist) => adminSpecialist || prevAdminSpecialist);
    setSelectedReviewSpecialist((prevReviewSpecialist) => reviewSpecialist || prevReviewSpecialist);

    setIsConfirmOpen(false);
    setDialogMessage('');
  }, [updateSpecialists]);

  const openDialog = useCallback((specialistType: string, currentSpecialistName: string, newSpecialistName: string, adminSpecialist?: SpecialistOption, reviewSpecialist?: SpecialistOption) => {
    setDialogMessage(`Are you sure you want to change the ${specialistType} Specialist from ${currentSpecialistName} to ${newSpecialistName}?`);
    setIsConfirmOpen(true);

    return () => {
      return confirmUpdateSpecialists(adminSpecialist, reviewSpecialist);
    };
  }, [confirmUpdateSpecialists]);

  const findSpecialistOption = (specialistValue: string, prop: keyof SpecialistOption, specialistOptions: SpecialistOption[]) => {
    return specialistOptions.find(specialist => specialist[prop] === specialistValue);
  };

  const handleAdminSpecialistChange = useCallback((optionValue?: string) => {
    const specialist = findSpecialistOption(optionValue, 'value', adminSpecialistOptions);

    if (specialist && specialist !== selectedAdminSpecialist) {
      handleConfirmRef.current = openDialog('Admin', selectedAdminSpecialist?.label ?? 'none', specialist.label, specialist);
    }
  }, [selectedAdminSpecialist, adminSpecialistOptions, openDialog]);

  const handleReviewSpecialistChange = useCallback((optionValue?: string) => {
    const specialist = findSpecialistOption(optionValue ?? '', 'value', reviewSpecialistOptions);

    if (specialist && specialist !== selectedReviewSpecialist) {
      handleConfirmRef.current = openDialog('Review', selectedReviewSpecialist?.label ?? 'none', specialist.label, undefined, specialist);
    }
  }, [selectedReviewSpecialist, reviewSpecialistOptions, openDialog]);

  useEffect(() => {
    if (loan && adminSpecialistOptions && reviewSpecialistOptions && !selectedAdminSpecialist && !selectedReviewSpecialist) {
      const adminSpecialist = findSpecialistOption(loan.drawAdminSpecialistFullName, 'label', adminSpecialistOptions);
      const reviewSpecialist = findSpecialistOption(loan.drawReviewSpecialistFullName, 'label', reviewSpecialistOptions);

      if (adminSpecialist) {
        setSelectedAdminSpecialist(adminSpecialist);
      }

      if (reviewSpecialist) {
        setSelectedReviewSpecialist(reviewSpecialist);
      }
    }
  }, [loan, selectedAdminSpecialist, selectedReviewSpecialist, adminSpecialistOptions, reviewSpecialistOptions]);

  return (
    <>
      <Paper title="Loan Actors" borderType={PaperBorderType.SOLID_BORDER}>
        <Grid
          container
          justifyContent='space-between'
          spacing={2}
          className={classes.gridContainer}
        >
          <Grid item xs={6} md={6} className={classes.selectFieldContainer}>
            {adminSpecialistOptions &&
              <SelectField
                standaloneLabel
                label='Admin Specialist'
                value={selectedAdminSpecialist?.value ?? ''}
                options={adminSpecialistOptions}
                onChange={handleAdminSpecialistChange}
                fullWidth
                testId='admin-specialist-select'
              />
            }
          </Grid>
          <Grid item xs={6} md={6} className={classes.selectFieldContainer}>
            {reviewSpecialistOptions &&
              <SelectField
                standaloneLabel
                label='Review Specialist'
                value={selectedReviewSpecialist?.value ?? ''}
                options={reviewSpecialistOptions}
                onChange={handleReviewSpecialistChange}
                fullWidth
                testId='review-specialist-select'
              />
            }
          </Grid>
        </Grid>
      </Paper>
      <ConfirmDialog
        open={isConfirmOpen}
        title={'Update Specialist'}
        body={dialogMessage}
        handleCancel={() => setIsConfirmOpen(false)}
        handleClose={() => setIsConfirmOpen(false)}
        handleConfirm={handleConfirmRef.current}
        confirmButtonText={'Yes'}
        cancelButtonText={'No'}
      />
    </>
  );
}
